import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, ArrowLeft, Trophy, Filter, X, ChevronDown,
  Users, MapPin, TrendingUp, Star, ExternalLink
} from 'lucide-react';
import { API, POSITION_OPTIONS } from '../lib/constants';

const ALL_POSITIONS = [...POSITION_OPTIONS.offense, ...POSITION_OPTIONS.defense, ...POSITION_OPTIONS.specialTeams];
const CLASS_YEARS = [2025, 2026, 2027, 2028, 2029];

const Directory = () => {
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [classYearFilter, setClassYearFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('recent'); // recent, offers, name
  const [viewMode, setViewMode] = useState('grid'); // grid, list

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (positionFilter) params.append('position', positionFilter);
      if (classYearFilter) params.append('class_year', classYearFilter);
      if (stateFilter) params.append('state', stateFilter);
      
      const res = await axios.get(`${API}/api/directory?${params.toString()}`);
      let results = res.data.profiles || [];
      
      // Client-side sorting
      if (sortBy === 'offers') results.sort((a, b) => (b.offers_count || 0) - (a.offers_count || 0));
      else if (sortBy === 'name') results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      setAthletes(results);
    } catch (err) {
      console.error('Directory fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAthletes();
  }, [positionFilter, classYearFilter, stateFilter, sortBy]);

  useEffect(() => {
    const timer = setTimeout(fetchAthletes, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const clearFilters = () => {
    setSearch('');
    setPositionFilter('');
    setClassYearFilter('');
    setStateFilter('');
  };

  const hasFilters = search || positionFilter || classYearFilter || stateFilter;
  const isLoggedIn = !!localStorage.getItem('userId');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-600">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#1a1a1a] rounded-full flex items-center justify-center">
                  <span className="text-[#16A34A] font-black text-xs">O</span>
                </div>
                <h1 className="font-black text-lg text-gray-900">Discover</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isLoggedIn && (
                <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} className="rounded-full text-xs">
                  Dashboard
                </Button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search players, schools, cities..."
              className="w-full bg-gray-50 border border-gray-200 text-gray-900 pl-10 pr-10 h-11 rounded-xl text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 focus:border-[#16A34A]"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter Chips */}
          <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide pb-1">
            {/* Position filter chips */}
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                hasFilters ? 'bg-[#16A34A]/10 border-[#16A34A]/30 text-[#1a1a1a]' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}>
              <Filter className="h-3 w-3" /> Filters {hasFilters && '•'}
            </button>
            
            {/* Quick position chips */}
            {['QB', 'RB', 'WR', 'TE', 'LB', 'DB', 'DL', 'OL'].map(pos => (
              <button key={pos} onClick={() => setPositionFilter(positionFilter === pos ? '' : pos)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-all ${
                  positionFilter === pos 
                    ? 'bg-[#0F5132] text-white border-[#16A34A]' 
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}>
                {pos}
              </button>
            ))}

            {/* Sort */}
            <button onClick={() => setSortBy(sortBy === 'offers' ? 'recent' : 'offers')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                sortBy === 'offers' ? 'bg-[#16A34A]/10 border-[#16A34A]/30 text-[#1a1a1a]' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}>
              <Trophy className="h-3 w-3" /> {sortBy === 'offers' ? 'Most Offers' : 'Sort'}
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="flex gap-2 mt-2 flex-wrap animate-in fade-in">
              <select value={positionFilter} onChange={e => setPositionFilter(e.target.value)}
                className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-3 py-2">
                <option value="">All Positions</option>
                <optgroup label="Offense">{POSITION_OPTIONS.offense.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
                <optgroup label="Defense">{POSITION_OPTIONS.defense.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
                <optgroup label="Special Teams">{POSITION_OPTIONS.specialTeams.map(p => <option key={p} value={p}>{p}</option>)}</optgroup>
              </select>
              <select value={classYearFilter} onChange={e => setClassYearFilter(e.target.value)}
                className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-3 py-2">
                <option value="">All Classes</option>
                {CLASS_YEARS.map(y => <option key={y} value={y}>Class of {y}</option>)}
              </select>
              {hasFilters && (
                <button onClick={clearFilters} className="text-[#16A34A] text-xs font-semibold flex items-center gap-1 px-2">
                  <X className="h-3 w-3" /> Clear all
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : athletes.length === 0 ? (
          <div className="text-center py-20">
            <Users className="h-16 w-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-gray-900 font-bold text-lg mb-1">No players found</h3>
            <p className="text-gray-400 text-sm mb-4">Try adjusting your filters or search terms</p>
            {hasFilters && (
              <Button onClick={clearFilters} variant="outline" size="sm" className="rounded-full">
                Clear filters
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-500 text-sm font-medium">{athletes.length} player{athletes.length !== 1 ? 's' : ''}</p>
              <div className="flex gap-1">
                <button onClick={() => setViewMode('grid')} 
                  className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-gray-200 text-gray-700' : 'text-gray-400'}`}>
                  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
                </button>
                <button onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-gray-200 text-gray-700' : 'text-gray-400'}`}>
                  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="3" rx="1"/><rect x="1" y="7" width="14" height="3" rx="1"/><rect x="1" y="12" width="14" height="3" rx="1"/></svg>
                </button>
              </div>
            </div>

            {/* Grid View */}
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {athletes.map(athlete => (
                  <button
                    key={athlete.id || athlete.username}
                    onClick={() => navigate(`/profile/${athlete.username}`)}
                    className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg hover:border-gray-200 transition-all text-left group"
                  >
                    {/* Photo */}
                    <div className="aspect-square bg-gradient-to-br from-[#16A34A]/10 to-[#16A34A]/5 relative overflow-hidden">
                      {athlete.profile_picture_url ? (
                        <img src={athlete.profile_picture_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-4xl font-black text-[#16A34A]/40">{athlete.name?.charAt(0) || '?'}</span>
                        </div>
                      )}
                      {/* Offer count badge */}
                      {athlete.offers_count > 0 && (
                        <div className="absolute top-2 right-2 bg-[#0F5132] text-white text-[10px] font-black px-2 py-0.5 rounded-full flex items-center gap-0.5">
                          <Trophy className="h-2.5 w-2.5" /> {athlete.offers_count}
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-3">
                      <p className="font-bold text-gray-900 text-sm truncate">{athlete.name}</p>
                      <p className="text-gray-500 text-xs truncate">{athlete.school}</p>
                      {(athlete.city || athlete.state) && (
                        <p className="text-gray-400 text-[10px] flex items-center gap-0.5 mt-0.5">
                          <MapPin className="h-2.5 w-2.5" /> {[athlete.city, athlete.state].filter(Boolean).join(', ')}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex gap-1">
                          {athlete.positions?.slice(0, 2).map((pos, i) => (
                            <span key={i} className="bg-[#16A34A]/10 text-[#1a1a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">
                              {pos}
                            </span>
                          ))}
                        </div>
                        <span className="text-gray-400 text-[10px]">'{String(athlete.class_year || '').slice(-2)}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              /* List View */
              <div className="space-y-2">
                {athletes.map(athlete => (
                  <button
                    key={athlete.id || athlete.username}
                    onClick={() => navigate(`/profile/${athlete.username}`)}
                    className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 hover:shadow-md hover:border-gray-200 transition-all text-left"
                  >
                    <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-[#16A34A]/10 to-[#16A34A]/5 flex-shrink-0">
                      {athlete.profile_picture_url ? (
                        <img src={athlete.profile_picture_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-xl font-black text-[#16A34A]/40">{athlete.name?.charAt(0) || '?'}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 text-sm truncate">{athlete.name}</p>
                        {athlete.offers_count > 0 && (
                          <Badge className="bg-[#16A34A]/10 text-[#1a1a1a] border-[#16A34A]/30 text-[10px] px-1.5 py-0">
                            <Trophy className="h-2.5 w-2.5 mr-0.5" />{athlete.offers_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs truncate">
                        {athlete.school} • Class of {athlete.class_year}
                        {(athlete.city || athlete.state) && ` • ${[athlete.city, athlete.state].filter(Boolean).join(', ')}`}
                      </p>
                      <div className="flex gap-1 mt-1">
                        {athlete.positions?.slice(0, 3).map((pos, i) => (
                          <span key={i} className="bg-gray-100 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded">
                            {pos}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {athlete.height && <p className="text-gray-900 text-xs font-semibold">{athlete.height}</p>}
                      {athlete.weight && <p className="text-gray-400 text-[10px]">{athlete.weight} lbs</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom CTA for non-logged-in users */}
      {!isLoggedIn && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-200 p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <p className="font-bold text-gray-900 text-sm">Join these players on Offersz</p>
              <p className="text-gray-500 text-xs">Create your profile in 60 seconds</p>
            </div>
            <Button onClick={() => navigate('/create-profile')} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold rounded-full">
              Get Started
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Directory;
